import { createContext, useContext, ReactNode, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { VersionNotification } from '@/types/version';
import { versionApi } from '@/lib/axios/versionApi';

const POLL_INTERVAL_MS = 60000; // 60s

interface VersionContextType {
  versionNotification: VersionNotification | null;
  dismissNotification: () => void;
  fetchLatestVersionAndApply: () => Promise<void>;
}

const APP_VERSION_KEY = 'appVersion';

const VersionContext = createContext<VersionContextType | undefined>(undefined);

export function VersionProvider({ children }: { children: ReactNode }) {
  const [versionNotification, setVersionNotification] = useState<VersionNotification | null>(null);
  const [currentVersion, setCurrentVersion] = useState<string | null>(() => {
    try {
      return localStorage.getItem(APP_VERSION_KEY);
    } catch {
      return null;
    }
  });

  const dismissNotification = useCallback(() => {
    setVersionNotification(null);
  }, []);

  const applyNewVersionNotification = useCallback((notification: VersionNotification, isReload = false) => {
    const storedVersion = currentVersion;

    // If already on this version, do nothing (prevents reload loop even when forceUpdate=true)
    if (storedVersion && storedVersion === notification.version) {
      return;
    }

    // Initialize baseline version if none exists yet
    if (!storedVersion) {
      try {
        localStorage.setItem(APP_VERSION_KEY, notification.version);
        setCurrentVersion(notification.version);
      } catch {
        // ignore storage errors
      }
      // For force update on first install: just save version, don't reload
      // The app is already running the latest version, no need to reload
      return;
    }

    setVersionNotification(notification);

    if (notification.isForceUpdate) {
      try {
        localStorage.setItem(APP_VERSION_KEY, notification.version);
        setCurrentVersion(notification.version);
      } catch {
        // ignore storage errors
      }

      setTimeout(() => {
        // Only reload if isReload is true
        if (!isReload) return;
        window.dispatchEvent(new Event('dashboardChanged'));
        setVersionNotification(null);
        console.log('[VersionContext] Reloading tab: force update to version', notification.version, { isForceUpdate: notification.isForceUpdate });
        window.location.reload();
      }, 2000);
    }
  }, [currentVersion]);

  const fetchLatestVersionAndApply = useCallback(async () => {
    try {
      const data = await versionApi.getLatest();
      if (!data) return;
      applyNewVersionNotification({
        version: data.version,
        description: data.description,
        isForceUpdate: data.isForceUpdate,
        createdAt: data.createdAt,
      }, true);
    } catch (error) {
      // Silently ignore fetch errors to avoid blocking UI
      console.warn('Version check failed', error);
    }
  }, [applyNewVersionNotification]);

  const fetchLatestVersionAndApplyRef = useRef(fetchLatestVersionAndApply);
  useEffect(() => {
    fetchLatestVersionAndApplyRef.current = fetchLatestVersionAndApply;
  }, [fetchLatestVersionAndApply]);

  // Initial check on mount
  useEffect(() => {
    fetchLatestVersionAndApplyRef.current();
  }, []);

  // Periodic polling for new version
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchLatestVersionAndApplyRef.current();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, []);

  // Re-check when tab regains focus/visibility or network comes back
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchLatestVersionAndApplyRef.current();
      }
    };
    const handleOnline = () => {
      fetchLatestVersionAndApplyRef.current();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  const contextValue = useMemo(
    () => ({ versionNotification, dismissNotification, fetchLatestVersionAndApply }),
    [versionNotification, dismissNotification, fetchLatestVersionAndApply]
  );

  return (
    <VersionContext.Provider value={contextValue}>
      {children}
    </VersionContext.Provider>
  );
}

export function useVersion() {
  const context = useContext(VersionContext);
  if (context === undefined) {
    throw new Error('useVersion must be used within a VersionProvider');
  }
  return context;
}
